import { Grid, List, Search } from "lucide-react";

import { AdminFilterBar } from "@/components/admin/AdminSurface";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

type StatusFilter = "all" | "pending" | "approved" | "rejected" | "unassigned";

interface AdminReviewFiltersProps {
  searchTerm: string;
  setSearchTerm: (value: string) => void;
  statusFilter: StatusFilter;
  setStatusFilter: (value: StatusFilter) => void;
  viewMode: "grid" | "list";
  setViewMode: (value: "grid" | "list") => void;
  statusCounts: Record<StatusFilter, number>;
  filteredCount: number;
  totalCount: number;
  onReset: () => void;
}

const filterLabels: Record<StatusFilter, string> = {
  all: "All",
  pending: "Pending",
  approved: "Approved",
  rejected: "Rejected",
  unassigned: "Unassigned",
};

const AdminReviewFilters = ({
  searchTerm,
  setSearchTerm,
  statusFilter,
  setStatusFilter,
  viewMode,
  setViewMode,
  statusCounts,
  filteredCount,
  totalCount,
  onReset,
}: AdminReviewFiltersProps) => {
  return (
    <AdminFilterBar>
      <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
        <div className="w-full max-w-md">
          <label className="mb-2 block text-sm font-medium text-gray-700">Search listings</label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <Input
              type="search"
              placeholder="Search by listing, owner, category, or area"
              className="h-11 rounded-lg border-gray-200 bg-white pl-9"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
            />
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant={viewMode === "list" ? "default" : "outline"}
            size="sm"
            onClick={() => setViewMode("list")}
            className={
              viewMode === "list"
                ? "rounded-lg bg-whatsapp text-white hover:bg-whatsapp-dark"
                : "rounded-lg border-gray-200 text-gray-700 hover:border-whatsapp hover:text-whatsapp"
            }
          >
            <List className="h-4 w-4" />
            List view
          </Button>
          <Button
            type="button"
            variant={viewMode === "grid" ? "default" : "outline"}
            size="sm"
            onClick={() => setViewMode("grid")}
            className={
              viewMode === "grid"
                ? "rounded-lg bg-whatsapp text-white hover:bg-whatsapp-dark"
                : "rounded-lg border-gray-200 text-gray-700 hover:border-whatsapp hover:text-whatsapp"
            }
          >
            <Grid className="h-4 w-4" />
            Grid view
          </Button>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        {(Object.keys(filterLabels) as StatusFilter[]).map((filterKey) => (
          <Button
            key={filterKey}
            type="button"
            variant={statusFilter === filterKey ? "default" : "outline"}
            size="sm"
            onClick={() => setStatusFilter(filterKey)}
            className={
              statusFilter === filterKey
                ? "rounded-lg bg-whatsapp text-white hover:bg-whatsapp-dark"
                : "rounded-lg border-gray-200 text-gray-700 hover:border-whatsapp hover:text-whatsapp"
            }
          >
            {filterLabels[filterKey]}
            <span className="ml-1 rounded-full bg-black/10 px-2 py-0.5 text-[11px] font-semibold text-current">
              {statusCounts[filterKey]}
            </span>
          </Button>
        ))}
      </div>

      <div className="mt-4 flex flex-col gap-2 text-sm text-gray-500 sm:flex-row sm:items-center sm:justify-between">
        <p>
          Showing {filteredCount} of {totalCount} listings.
        </p>
        {(searchTerm || statusFilter !== "pending" || viewMode !== "list") && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-8 rounded-lg px-0 text-gray-600 hover:bg-transparent hover:text-whatsapp"
            onClick={onReset}
          >
            Reset to pending list
          </Button>
        )}
      </div>
    </AdminFilterBar>
  );
};

export default AdminReviewFilters;
